import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, Optional } from '@angular/core';
import { CONSENT_LABELS } from '@hohr/components';
import { App, PortalService } from '@hohr/portal-launcher';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, first, map } from 'rxjs/operators';
import { API_BASE_URL } from './core/data-access/api';
import { ProfileService } from './core/services/profile.service';
import { User } from './core/models/user.models';
import { MembershipService } from './core/services/membership.service';
import { ConfigService, Culture } from '@hohr/config';
import { ClientConfig } from './shared/interfaces/config.interface';

@Injectable({
  providedIn: 'root',
})
export class AppFacade {
  private readonly profilePhotoSubject$ = new BehaviorSubject<File | Blob>(
    null
  );
  profilePhoto$ = this.profilePhotoSubject$.asObservable();

  private readonly currentLanguage$ = new BehaviorSubject<string>(null);

  private readonly currentLanguageIdSubject = new BehaviorSubject<string>(null);
  currentLanguageId$ = this.currentLanguageIdSubject.asObservable();

  readonly myprofileURI$ = new BehaviorSubject<string>(null);
  readonly userProfile$ = new BehaviorSubject<User>(null);

  get currentLanguage() {
    return this.currentLanguage$.getValue();
  }

  set currentLanguage(lang: string) {
    this.currentLanguage$.next(lang);
    localStorage.setItem('appLanguage', lang);
  }

  get currentLanguageId() {
    return this.currentLanguageIdSubject.getValue();
  }

  set currentLanguageId(id: string) {
    this.currentLanguageIdSubject.next(id);
    localStorage.setItem('appLanguageId', id);
  }

  get socialLogins() {
    return (
      this.configService?.config?.security.auth0?.connections?.social || []
    );
  }

  get cultures() {
    return this.configService?.config?.cultures ?? [];
  }

  get cultureCodes() {
    return (
      this.configService?.config?.cultures?.map(
        (culture) => culture.cultureCode
      ) ?? []
    );
  }

  constructor(
    private translateService: TranslateService,
    private profileService: ProfileService,
    private portalService: PortalService,
    private membershipService: MembershipService,
    private configService: ConfigService<ClientConfig>,
    @Inject(HttpClient) private http: HttpClient,
    @Optional() @Inject(API_BASE_URL) private baseUrl?: string
  ) {
    this.http = http;
    this.baseUrl = baseUrl;
  }

  getProfilePhoto(): Observable<void> {
    return this.profileService.getProfilePicture().pipe(
      first(),
      map((photo: File | Blob) => this.profilePhotoSubject$.next(photo))
    );
  }

  getConsentTranslations(): { [key: string]: string } {
    return CONSENT_LABELS.reduce(
      (x, y) => ((x[y] = this.translateService.instant(y) || y), x),
      {}
    );
  }

  getErrorMessageTranslations(): { [key: string]: string } {
    return [
      'error.userExists',
      'error.phoneExists',
      'error.required',
      'error.invalidEmail',
      'error.validatePhoneNumber',
      'error.verificationCode',
      'error.invalidPassword',
    ].reduce(
      (a, x) => ({
        ...a,
        [x.replace('error.', '')]: this.translateService.instant(x) || x,
      }),
      {}
    );
  }

  setCurrentCulture(culture: Culture) {
    this.currentLanguage = culture.cultureCode;
    this.currentLanguageId = culture.languageId;
    this.setDefaultLanguage(culture.cultureCode);
  }

  getMyProfileUri() {
    return this.portalService.getApps().pipe(
      map((apps: App[]) => {
        const myprofileApp = apps.find((app) => app.code === '002');
        if (myprofileApp) {
          this.myprofileURI$.next(myprofileApp.uri);
        }
      })
    );
  }

  getUserProfile(): Observable<void> {
    return this.membershipService.getUserProfile().pipe(
      map((user: User) => {
        return this.userProfile$.next({
          ...user,
          userName: `${user.firstName} ${user.lastName}`,
        });
      }),
      catchError(() => {
        return of(null);
      })
    );
  }

  getCultureByCultureCode(cultureCode): Culture {
    return this.cultures.find(
      (culture) =>
        culture.cultureCode.toLowerCase() === cultureCode.toLowerCase()
    );
  }

  getAlpha2CodeFromCulture(language: string): string {
    return this.cultures
      .find(
        (culture) =>
          culture.cultureCode.toLowerCase() === language.toLowerCase()
      )
      .languageAlpha2Code.toLowerCase();
  }

  getCultureByAlpha2Code(language: string): Culture {
    return this.cultures.find(
      (culture) =>
        culture.languageAlpha2Code.toLowerCase() === language.toLowerCase()
    );
  }

  setDefaultLanguage(cultureCode: string): void {
    this.translateService.setDefaultLang(cultureCode);
    this.translateService.use(cultureCode);
  }
}
